import type { AdAdapter, AdEvent } from "./types";

/**
 * ChatGPT Ads, to count the sign-ups and plans an ad brought: OpenAI's
 * Measurement Pixel (developers.openai.com/ads/measurement-pixel), one
 * `registration_completed` for each account made and one
 * `subscription_created` for each paid plan started. Nothing else is
 * sent: no page views, no packs, no user data, and each event opts out of
 * personalisation.
 *
 * The pixel's automatic advanced matching, which hashes what it finds on
 * the page, is a switch in Ads Manager and stays off there.
 *
 * When it may run is the relay's rule (lib/ads/relay.ts). Until then no
 * OpenAI script loads; once it has, going off sets the pixel's consent to
 * false, which sends nothing more and removes its cookies.
 */

/** OpenAI's SDK, which replaces the queue below with itself as it loads. */
export const OPENAI_PIXEL_SDK = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

type Oaiq = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
    interface Window {
        oaiq?: Oaiq;
    }
}

export function openaiAdapter(pixelId: string): AdAdapter {
    let started = false;

    function oaiq(...args: unknown[]): void {
        if (!started) return;
        window.oaiq?.(...args);
    }

    return {
        name: "openai",
        /**
         * A sign-up, and a paid plan. A return that names no paid tier
         * (`planId` null: an older Checkout's, or an address made up) is not
         * counted, so every `subscription_created` is a paid plan.
         */
        accepts: (event) =>
            event.kind === "signup" ||
            (event.kind === "plan" && "planId" in event && Boolean(event.planId)),
        /**
         * OpenAI's install snippet, as code: the queue the SDK reads, consent
         * set to false before `init` as its docs ask, then the SDK itself.
         */
        load() {
            if (started) return;
            started = true;
            if (!window.oaiq) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const queue: Oaiq = function (..._args: unknown[]) {
                    // The snippet queues each call's `arguments` object, as the SDK expects.
                    // eslint-disable-next-line prefer-rest-params
                    queue.q?.push(arguments);
                };
                queue.q = [];
                window.oaiq = queue;
            }
            oaiq("consent", false);
            oaiq("init", { pixelId });
            const script = document.createElement("script");
            script.async = true;
            script.src = OPENAI_PIXEL_SDK;
            document.head.appendChild(script);
        },
        grant: () => oaiq("consent", true),
        revoke: () => oaiq("consent", false),
        send(event) {
            if (event.kind === "signup") {
                oaiq(
                    "measure",
                    "registration_completed",
                    { type: "customer_action" },
                    measureOptions(event),
                );
                return;
            }
            const planId = "planId" in event ? event.planId : null;
            if (!planId) return;
            oaiq(
                "measure",
                "subscription_created",
                {
                    type: "plan_enrollment",
                    plan_id: planId,
                    ...(event.value !== null && event.currency
                        ? { amount: Math.round(event.value * 100), currency: event.currency }
                        : {}),
                },
                measureOptions(event),
            );
        },
    };
}

/** The id that makes an event one, and the opt-out of personalisation every event carries. */
function measureOptions(event: AdEvent) {
    return { ...(event.transactionId ? { event_id: event.transactionId } : {}), opt_out: true };
}
